import React, { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { HelpBubble } from "../components/HelpBubble";
import { Loader2 } from "lucide-react";
import { StatsCards } from "../components/dashboard/StatsCards";
import { RenewalsWidget } from "../components/dashboard/RenewalsWidget";
import { ContractCharts } from "../components/dashboard/ContractCharts";
import { RecentContracts } from "../components/dashboard/RecentContracts";

export default function DashboardPage() {
  const { canDo, user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [renewals, setRenewals] = useState(null);
  const [loading, setLoading] = useState(true);

  const mgmtPerm = canDo("management.view") || (user?.action_permissions?.["management.view"] && user.action_permissions["management.view"] !== "no");

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await apiClient.get("/analytics/dashboard");
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRenewals = useCallback(async () => {
    try {
      const response = await apiClient.get("/renewals?days=90");
      setRenewals(response.data);
    } catch (e) {
      /* Renewals widget is non-critical */
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    if (mgmtPerm) fetchRenewals();
  }, [fetchAnalytics, fetchRenewals, mgmtPerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Dashboard <HelpBubble tip="Overview of your contract portfolio — key metrics, recent activity, and status distribution at a glance." />
        </h1>
        <p className="text-slate-500 mt-1">Contract management overview</p>
      </div>

      <StatsCards analytics={analytics} />
      {mgmtPerm && <RenewalsWidget renewals={renewals} />}
      <ContractCharts analytics={analytics} />
      <RecentContracts contracts={analytics?.recent_contracts} />
    </div>
  );
}
